/**
 * qualityLevel — o NÍVEL de qualidade resolvido no BOOT, e o que depende dele
 * antes da cena existir.
 *
 * Por que este módulo existe (e por que ele não tem dependência nenhuma):
 * o painel de Configurações só é instanciado em main.js DEPOIS que a cidade
 * inteira já foi montada e assada pelo batchStatic — quando o nível chega lá, os
 * canvases do chão, das fachadas e das manchas de sombra já foram pintados e as
 * malhas de origem, descartadas. Tudo que muda a RESOLUÇÃO DE TEXTURA precisa,
 * portanto, ser decidido no primeiro import. É o que este arquivo faz, e é a
 * razão de trocar de/para "ultra" no painel RECARREGAR a página (mesmo caminho
 * que a troca de MSAA e a troca de cidade já usam).
 *
 * Fora do browser (harnesses em Node) não há `location` nem `localStorage`:
 * a resolução cai em 'alta' e as constantes ficam nos valores de sempre — os
 * harnesses que importam config.js continuam vendo os mesmos números.
 */

/** Níveis manuais, em ordem de custo DECRESCENTE (é o contrato do postFxHarness). */
export const QUALITY_LEVELS = ['ultra', 'alta', 'equilibrada', 'desempenho'];

/** Níveis aceitos pela URL/painel (inclui a resolução automática). */
export const QUALITY_CHOICES = [...QUALITY_LEVELS, 'auto'];

export const DEFAULT_QUALITY = 'alta';

/**
 * TELAS SECUNDÁRIAS que montam a mesma cidade mas NÃO têm o painel de
 * qualidade: /me (a visão do jogador, no celular dele) e /models.
 *
 * Elas ignoram o localStorage de propósito. O 'wab.quality' é gravado pelo
 * painel do TELÃO e o storage é por ORIGEM, não por tela: sem esta exceção,
 * deixar o telão em Ultra faria o /me aberto no mesmo navegador subir a textura
 * do chão para 4620×4480 e mirar pixelRatio 2 — num celular de dpr 3 isso é
 * ~3M px por passe mais ~110 MB de textura, exatamente o oposto do `?fps=30` de
 * economia que essa tela documenta. O `?q=` continua valendo: ali a escolha é
 * explícita e de quem abriu a URL.
 */
const SUBVIEW = /\/(me|models)(\/|\/index\.html)?$/;

/**
 * Nível escolhido: `?q=` na URL (fixa um kiosk sem mexer em storage) >
 * localStorage 'wab.quality' > 'alta'. Mesma precedência do painel — e é o
 * painel que passou a importar ESTA função, para não existirem duas verdades.
 */
export function readQualityLevel() {
    try {
        const q = new URLSearchParams(location.search).get('q');
        if (q && QUALITY_CHOICES.includes(q)) return q;
    } catch { /* sem location (Node) */ }
    try {
        if (SUBVIEW.test(location.pathname)) return DEFAULT_QUALITY;
    } catch { /* sem location (Node) */ }
    try {
        const v = localStorage.getItem('wab.quality');
        if (v && QUALITY_CHOICES.includes(v)) return v;
    } catch { /* sem storage */ }
    return DEFAULT_QUALITY;
}

/** Nível com que ESTA sessão carregou (congelado no primeiro import). */
export const BOOT_QUALITY = readQualityLevel();

/** Atalho: o boot pediu o nível Ultra? (o que libera o tier de textura abaixo) */
export const IS_ULTRA = BOOT_QUALITY === 'ultra';

/**
 * TIER DE TEXTURA — só existe em Ultra, e só no boot.
 *
 * Estes são os números que atacam a pixelação LITERAL (falta de texel), que
 * nenhum antialiasing resolve: numa tela 4K a mesma textura cobre o dobro de
 * pixels em cada eixo, então o que estava calibrado para 1080p entra em
 * MAGNIFICAÇÃO e vira borrão.
 *
 *   groundPx   9 → 14 px/unidade. O chão é a maior superfície da tela e a de
 *              menor densidade: a 1920 px ele dá 1,10 texel/px (perfeito), a
 *              3840 px dá 0,55 (1,8× magnificado) e no fecho da câmera dinâmica
 *              passa de 4×. Com 14 o canvas vai de 2970×2880 para 4620×4480.
 *   facadeK    22 → 33 px/unidade nas fachadas GENÉRICAS (os prédios nomeados
 *              têm janela de geometria 3D real e não dependem disto). 22 é
 *              calibração de 1080p; no zoom da câmera dinâmica em 4K dá 0,57
 *              texel/px e a moldura das janelas some.
 *   blobPx     64 → 256 na mancha de sombra do avatar: a do carro chega a ~100 px
 *              de tela em 4K contra uma textura de 64 — magnificação pura.
 *   canopyLod  a copa simplificada dos arbustos pequenos (IcosahedronGeometry
 *              detail 0) tem NORMAIS PLANAS: é o único facetamento de verdade da
 *              cena. Em Ultra ela volta a detail 1 (+125 mil triângulos).
 */
export const TEX_TIER = IS_ULTRA
    ? { groundPx: 14, facadeK: 33, blobPx: 256, canopyLodDetail: 1 }
    : { groundPx: 9, facadeK: 22, blobPx: 64, canopyLodDetail: 0 };

/**
 * ORÇAMENTO de pixels do backbuffer em Ultra. Sem um teto absoluto, "renderizar
 * acima da tela" numa TV 4K pediria 7680×4320 = 33M px — mais de 1 GB só de
 * alvos de render, ou seja, perda de contexto WebGL garantida numa GPU
 * integrada. 9M px é ~4,3× o quadro de 1080p e cabe com folga em placa dedicada.
 */
export const ULTRA_BUDGET_PX = 9.0e6;

/**
 * RESOLUÇÃO INTERNA — decide o pixelRatio do renderer a partir do perfil.
 *
 * Duas semânticas, e é isso que faz o Ultra existir:
 *
 *   maxPixelRatio ≤ 1  →  TETO (o comportamento de sempre). `min(dpr, teto)`:
 *      "alta" com teto 1.0 nunca renderiza acima da resolução CSS.
 *
 *   maxPixelRatio > 1  →  ALVO. `max(dpr, alvo)`: o render nunca fica ABAIXO da
 *      resolução física da tela e, quando cabe no orçamento, fica ACIMA
 *      (supersampling — o browser reduz na composição, que é antialiasing de
 *      verdade, o único que também alcança o overlay dos nomes e as linhas
 *      conectoras, que rodam fora do composer e nenhum passe de AA toca).
 *
 * O caso que provavelmente motivou o pedido é o primeiro: numa tela 4K com a
 * escala do Windows em 150% o `devicePixelRatio` é 1,5 e `min(1.5, 1.0)` cria o
 * canvas a 2560×1440 para uma tela de 3840×2160 — o compositor ESTICA tudo em
 * 1,5×. Ali o Ultra é quase de graça: só devolve o 1:1 que a tela já tinha.
 *
 * Dois tetos protegem o Ultra de si mesmo (e SÓ existem neste ramo, para que os
 * perfis de sempre continuem com a expressão idêntica à de antes):
 *   • orçamento de pixels (ULTRA_BUDGET_PX);
 *   • maxTextureSize da GPU — passar disso faz o browser encolher o drawing
 *     buffer em silêncio (imagem borrada) e os alvos do composer falharem.
 * Nenhum dos dois pode deixar o Ultra PIOR que "alta": o piso é o que "alta"
 * daria nesta mesma tela.
 *
 * Função pura (sem DOM) de propósito — é o que o scripts/ultraResHarness.mjs
 * dirige com a matriz de telas do evento.
 */
export function resolvePixelRatio({ dpr, cssW, cssH, maxPixelRatio, maxTextureSize = 8192, budgetPx = ULTRA_BUDGET_PX }) {
    const d = dpr > 0 ? dpr : 1;
    if (!(maxPixelRatio > 1)) return Math.min(d, maxPixelRatio); // perfis de sempre: TETO
    const w = Math.max(1, cssW);
    const h = Math.max(1, cssH);
    const alvo = Math.max(d, maxPixelRatio);
    const piso = Math.min(d, 1);                      // Ultra nunca abaixo do que "alta" daria
    const capTex = maxTextureSize / Math.max(w, h);
    const capOrc = Math.sqrt(budgetPx / (w * h));
    return Math.min(alvo, Math.min(capTex, Math.max(piso, capOrc)));
}
